import React, { useState } from "react";

/**
 * Exclude a transaction from the reconciling population.
 *
 * Backend contract (`POST /api/transactions/{id}/exclude`) accepts a
 * fixed enum of exclusion types and a required reason. Prior UX was a
 * chained `window.prompt()` which offered no discovery of valid values;
 * this dialog surfaces the enum as a governed dropdown so the controller
 * can't accidentally submit a bad type.
 */
const EXCLUSION_TYPES = [
  {
    value: "duplicate_confirmed",
    label: "Duplicate — confirmed",
    hint: "This row is a proven duplicate of another line and should be dropped from the certified population. Reversible.",
  },
  {
    value: "wrong_period",
    label: "Wrong period",
    hint: "The row belongs to a different close period (early/late-posted entry).",
  },
  {
    value: "import_error",
    label: "Import error",
    hint: "A defect in the source file caused this row to be created (bad OCR, malformed export, etc.).",
  },
  {
    value: "controller_override",
    label: "Controller override",
    hint: "None of the above cleanly apply and the controller is documenting a governance decision to exclude.",
  },
  {
    value: "parser_artifact",
    label: "Parser artefact",
    hint: "The row exists only because of an import-parser quirk (header row, footer, section marker) and is not a real transaction.",
  },
];

export function ExcludeDialog({ transaction, onConfirm, onCancel }) {
  const [type, setType] = useState("controller_override");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!transaction) return null;

  const chosen = EXCLUSION_TYPES.find((t) => t.value === type);
  const canSubmit = reason.trim().length > 0 && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm({ type, reason: reason.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  const overlay = {
    position: "fixed", inset: 0, background: "rgba(15,20,30,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999,
  };
  const shell = {
    background: "#fff", maxWidth: 520, width: "92%",
    borderRadius: 8, padding: "22px 24px",
    boxShadow: "0 12px 36px rgba(0,0,0,0.18)",
    fontFamily: "system-ui, -apple-system, sans-serif", color: "#1f2937",
  };

  return (
    <div data-testid="exclude-dialog" style={overlay}
         onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}>
      <form onSubmit={submit} style={shell}>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
          Exclude transaction
        </h3>
        <p style={{ marginTop: 6, marginBottom: 14, fontSize: 12.5, color: "#4b5563" }}>
          <b>{(transaction.description || "").slice(0, 90)}</b>
          {" · "}${Number(transaction.amount_unsigned || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {" · "}{transaction.dr_cr}
        </p>

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="exclude-type"
                 style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
            Exclusion type
          </label>
          <select id="exclude-type"
                  data-testid="exclude-type-select"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    fontSize: 14, padding: "8px 10px",
                    border: "1px solid #d1d5db", borderRadius: 6,
                    background: "#fff",
                  }}>
            {EXCLUSION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {chosen?.hint && (
            <div style={{
              marginTop: 6, fontSize: 11.5, color: "#6b7280",
              lineHeight: 1.45,
            }}>
              {chosen.hint}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="exclude-reason"
                 style={{ display: "block", fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
            Reason (required)
          </label>
          <textarea id="exclude-reason"
                    data-testid="exclude-reason-input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    autoFocus
                    rows={3}
                    placeholder="Explain why this row is being excluded. This is written to the audit trail."
                    style={{
                      width: "100%", boxSizing: "border-box",
                      fontSize: 13, padding: "8px 10px",
                      border: "1px solid #d1d5db", borderRadius: 6,
                      fontFamily: "inherit", resize: "vertical",
                    }} />
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button"
                  data-testid="exclude-cancel-btn"
                  onClick={onCancel}
                  disabled={submitting}
                  style={{
                    padding: "7px 14px", borderRadius: 6, border: "1px solid #d1d5db",
                    background: "#fff", color: "#374151", fontSize: 13,
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}>
            Cancel
          </button>
          <button type="submit"
                  data-testid="exclude-confirm-btn"
                  disabled={!canSubmit}
                  style={{
                    padding: "7px 14px", borderRadius: 6, border: "none",
                    background: canSubmit ? "#b91c1c" : "#9ca3af",
                    color: "#fff", fontSize: 13, fontWeight: 600,
                    cursor: canSubmit ? "pointer" : "not-allowed",
                  }}>
            {submitting ? "Excluding…" : "Exclude"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ExcludeDialog;
